/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { SoftwareImpactSeverity, SoftwareQuality } from '~shared/types/clean-code-taxonomy';

export enum JiraPermission {
  CREATE = 'issue-create',
  DELETE = 'issue-delete',
  BIND_PROJECT = 'project-bind',
}

export interface JiraOrganizationBindingPostResponse {
  binding?: JiraOrganizationBinding;
  resources: AtlassianAccessibleResource[];
}

export interface JiraOrganizationBinding {
  createdAt: string;
  createdBy: string;
  id: string;
  // SQS specific, but later may be added to cloud
  isTokenShared?: boolean;
  jiraInstanceUrl: string;
  sonarOrganizationUuid: string;
}

export interface JiraOrganizationBindingPostPayload {
  authorizationCode: string;
  state: string;
}

export interface JiraOrganizationBindingPatchPayload {
  jiraCloudId: string;
  jiraInstanceUrl: string;
  sonarOrganizationUuid: string;
}

export interface JiraOrganizationPropertiesPatchPayload {
  isTokenShared: boolean;
  sonarOrganizationUuid: string;
}

export interface AtlassianAuthUrlPayload {
  sonarOrganizationKey: string;
  sonarOrganizationUuid: string;
}

export interface AtlassianAccessibleResource {
  cloudId: string;
  url: string;
}

export interface AtlassianApplicationConfigurationPayload {
  clientId: string;
  secret: string;
}

export interface AtlassianApplicationConfigurationResponse {
  clientId: string;
}

export interface JiraProjectBinding {
  id: string;
  jiraOrganizationId: string;
  jiraProjectKey: string;
  sonarProjectId: string;
}

export interface JiraProjectSummary {
  name: string;
  openIssuesCount: number;
  releaseDate?: string;
  url: string;
}

export interface JiraProject {
  key: string;
  name: string;
}

export interface JiraWorkType {
  description: string;
  fields: JiraWorkTypeField[];
  hierarchyLevel: number;
  id: string;
  name: string;
  selected: boolean;
  subtask: boolean;
}

export interface JiraWorkTypeField {
  hasDefaultValue: boolean;
  key: string;
  name: string;
  required: boolean;
}

export interface JiraSelectedWorkTypes {
  selectedWorkTypes: string[];
  sonarProjectId: string;
}

export interface JiraWorkTypeQueryParams {
  includeFields: boolean;
  jiraProjectKey: string;
  sonarOrganizationUuid: string;
  sonarProjectId: string;
}

export interface JiraWorkItem {
  id: string;
  jiraIssueId?: string; // Making this optional to handle the empty cache case
  jiraIssueKey: string;
  jiraIssueStatus?: string; // A work item from the bulk cache will not have a status briefly until the status is fetched
  jiraIssueUrl: string;
}

export interface JiraLinkedResource {
  jiraWorkItem: JiraWorkItem;
  resourceId: string;
  resourceType: JiraWorkItemResourceType;
}

export interface JiraWorkItemQueryParams {
  resourceId: string;
  resourceType: JiraWorkItemResourceType;
  sonarProjectId: string;
}

export interface JiraWorkItemUnlinkQueryParams {
  resourceId: string;
  resourceType: JiraWorkItemResourceType;
  sonarProjectId: string;
}

export interface JiraWorkItemPayload {
  description: string;
  /* resourceId should be skipped for bulk many to many case */
  resourceId?: string;
  resourceType: JiraWorkItemResourceType;
  sonarProjectId: string;
  summary: string;
  workTypeId: string;
}

export enum JiraWorkItemResourceType {
  SonarIssue = 'SONAR_ISSUE',
  DependencyRisk = 'DEPENDENCY_RISK',
}

export interface JiraBulkWorkItemsQueryParams {
  resourceType: JiraWorkItemResourceType;
  sonarIssueKeys: string[];
  sonarProjectId: string;
}

export enum JiraBulkWorkItemCreatePayloadMode {
  ManyToOne = 'many-to-one',
  ManyToMany = 'many-to-many',
}

interface JiraBulkWorkItemCreatePayloadBase {
  sonarIssues: {
    componentPath: string;
    creationDate: string;
    description: string;
    key: string;
    language: string;
    message: string;
    projectKey: string;
    ruleKey: string;
    textRangeEndLine: number;
    textRangeStartLine: number;
    title: string;
    url: string;
  }[];
  sonarOrganizationKey: string;
  sonarProjectId: string;
  workTypeId: string;
}
interface JiraBulkWorkItemCreatePayloadManyToOne extends JiraBulkWorkItemCreatePayloadBase {
  mode: JiraBulkWorkItemCreatePayloadMode.ManyToOne;
}

interface JiraBulkWorkItemCreatePayloadManyToMany extends JiraBulkWorkItemCreatePayloadBase {
  mode: JiraBulkWorkItemCreatePayloadMode.ManyToMany;
  parentWorkItemId: string;
}

export type JiraBulkWorkItemCreatePayload =
  | JiraBulkWorkItemCreatePayloadManyToOne
  | JiraBulkWorkItemCreatePayloadManyToMany;

export interface JiraBulkWorkItemQueryResult {
  jiraIssueId: string;
  jiraIssueKey: string;
  jiraIssueUrl: string;
  sonarIssueKey: string;
}

export interface LinkedResourcesCount {
  count: number;
}

/**
 * Could be either a sonar issue or a dependency risk
 */
export interface LinkableIssue {
  componentPath?: string;
  creationDate: string;
  impacts: {
    severity: SoftwareImpactSeverity;
    softwareQuality: SoftwareQuality;
  }[];
  key: string;
  message: string;
  organization?: string;
  packageUrl?: string; // PURL for dependency risks
  projectKey: string;
  projectUuid: string; // Project LEGACY id
  ruleKey?: string;
  textRange?: {
    endLine: number;
    endOffset: number;
    startLine: number;
    startOffset: number;
  };
  url: string; // A permalink to the issue
}
